import { MAX_FILE_SIZE, type ConversationView as Conversation, type LocalMessage } from '@swchat/client-sdk';
import type { Project, TextEntity } from '@swchat/protocol';
import { useEffect, useMemo, useRef, useState, type DragEvent } from 'react';
import { receiptLabel } from '../lib/display';
import { useActivity, useClient, useFiles, useMessages, usePinned, useReceipts, useThreads, useTyping, useVisible } from '../lib/hooks';
import type { BubbleProps } from './Bubble';
import { Composer } from './Composer';
import { ConversationContext } from './ConversationContext';
import { ConversationHeader, type ConversationTab } from './ConversationHeader';
import { ConversationSearch } from './ConversationSearch';
import { ActivityList, ThreadList } from './ConversationTabs';
import { FilesTab } from './FilesTab';
import { sendErrorText } from './format';
import { LazyForwardDialog, LazyGroupDialog } from './lazy';
import { ImageAlbum } from './image-album';
import { MessageList, type DropHandlers } from './MessageList';
import { PinnedBar } from './PinnedBar';
import { ThreadPanel } from './ThreadPanel';

/**
 * Hội thoại đang mở (chuyển nguyên logic `ChatView` của screens/Main.tsx): tải tin, đánh dấu đã đọc, trả lời/sửa,
 * cuộn tới tin (trích dẫn, kết quả tìm kiếm), gửi file, gửi lại; bố cục đầu hội thoại · vùng tin · ô soạn · khung ngữ cảnh.
 */
export function ConversationView({
  conversation,
  conversations,
  projects,
  focusMessage,
  contextOpen,
  drawer,
  onToggleContext,
  onBack,
  onOpenExternal,
}: {
  conversation: Conversation;
  conversations: Conversation[];
  projects: Project[];
  focusMessage?: { id: string; n: number };
  contextOpen: boolean;
  /** < 1200px: khung ngữ cảnh là ngăn kéo phủ lên hội thoại (W1.4). */
  drawer: boolean;
  onToggleContext: (open: boolean) => void;
  onBack: () => void;
  /** W2.5 (`/embed`): "Mở trên 7wportal" gửi `open_external` cho portal. */
  onOpenExternal?: (url: string) => void;
}) {
  const client = useClient();
  const [messages, loadUntil, older] = useMessages(conversation.id);
  const receipts = useReceipts(conversation.id);
  const typing = useTyping(conversation.id);
  const visible = useVisible();
  const [editing, setEditing] = useState<LocalMessage>();
  const [replyTo, setReplyTo] = useState<LocalMessage>();
  const [forwarding, setForwarding] = useState<LocalMessage>();
  const [highlight, setHighlight] = useState<string>();
  /** Tin khớp tìm kiếm trong hội thoại đang chọn (giữ nổi bật tới khi đổi/đóng tìm kiếm). */
  const [searchHit, setSearchHit] = useState<string>();
  const [searching, setSearching] = useState(false);
  /** Đang ở gần đáy khung tin → tin mới tự cuộn xuống; đang đọc tin cũ thì không kéo xuống. */
  const nearBottom = useRef(true);
  const [error, setError] = useState<string>();
  const [safety, setSafety] = useState<string>();
  const [managing, setManaging] = useState(false);
  const [tab, setTab] = useState<ConversationTab>('chat');
  /** W2.2: luồng đang mở (id tin gốc) — hiện ở vị trí khung ngữ cảnh. */
  const [thread, setThread] = useState<string>();
  const pinned = usePinned(conversation.id);
  const threads = useThreads(conversation.id);
  const activity = useActivity(conversation.id);
  // W2.3: chỉ nạp/giải mã danh sách tệp khi tab Tệp đang mở (hội thoại nhiều ảnh không bị giải mã nền).
  const files = useFiles(tab === 'files' ? conversation.id : undefined);
  const pinnedIds = useMemo(() => new Set(pinned.map((p) => p.id)), [pinned]);
  const threadOf = useMemo(() => new Map(threads.map((t) => [t.root_id, t])), [threads]);
  const canPin = client.canPin(conversation.id);
  const bottom = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLTextAreaElement>(null);
  const stage = useRef<((files: File[]) => void) | null>(null);
  const focused = useRef<number>(0);
  /** C1: file đang mã hóa + tải lên (tiến độ 0..1). */
  const [upload, setUpload] = useState<{ name: string; progress: number }>();
  const [dragOver, setDragOver] = useState(false);
  const project = conversation.project_id ? projects.find((p) => p.id === conversation.project_id) : undefined;

  /**
   * C1: mã hóa từng khối trên máy rồi tải lên; ảnh kèm kích thước (đọc bằng createImageBitmap) để giữ chỗ khi hiển thị.
   * `threadRoot`: gửi vào luồng (W2.2 — nút đính kèm của khung luồng); lỗi hiện ở nơi gửi qua `onError`.
   */
  async function sendFiles(files: readonly File[], threadRoot?: string, onError: (e: string) => void = setError) {
    if (conversation.left || upload) return;
    setError(undefined);
    for (const file of files) {
      if (file.size > MAX_FILE_SIZE) {
        onError(`“${file.name}” vượt quá giới hạn 100 MB`);
        continue;
      }
      let dims: { width?: number; height?: number } = {};
      if (file.type.startsWith('image/')) {
        try {
          const bitmap = await createImageBitmap(file);
          dims = { width: bitmap.width, height: bitmap.height };
          bitmap.close();
        } catch {
          // Không đọc được ảnh (định dạng lạ) → gửi như file thường, không kèm kích thước.
        }
      }
      setUpload({ name: file.name, progress: 0 });
      try {
        await client.sendFile(conversation.id, {
          name: file.name,
          mime: file.type || 'application/octet-stream',
          data: file,
          ...dims,
          threadRoot,
          onProgress: (progress) => setUpload({ name: file.name, progress }),
        });
      } catch (err) {
        onError(sendErrorText(err));
      } finally {
        setUpload(undefined);
      }
    }
  }

  // C1: kéo-thả file vào khung chat.
  const drop: DropHandlers = conversation.left
    ? {}
    : {
        onDragOver: (e: DragEvent) => {
          if (!e.dataTransfer.types.includes('Files')) return;
          e.preventDefault();
          setDragOver(true);
        },
        onDragLeave: () => setDragOver(false),
        onDrop: (e: DragEvent) => {
          e.preventDefault();
          setDragOver(false);
          if (e.dataTransfer.files.length) stage.current?.([...e.dataTransfer.files]);
        },
      };

  // Mới mở hội thoại: mặc định dừng ở tin cuối và bám đáy trong lúc nội dung còn nạp (tin tải dần, ảnh / tệp đổi chiều cao,
  // giải mã xong mới có chữ…), cho tới khi người dùng tự cuộn / bấm / gõ phím hoặc sau vài giây. Mở từ kết quả tìm kiếm / trích
  // dẫn (có `focusMessage`) thì để logic cuộn tới tin đó lo.
  useEffect(() => {
    if (focusMessage) return;
    const scroller = bottom.current?.parentElement;
    if (!scroller) return;
    let stick = true;
    const toBottom = () => {
      if (stick) bottom.current?.scrollIntoView({ block: 'end' });
    };
    const stop = () => {
      stick = false;
    };
    const mo = new MutationObserver(toBottom);
    mo.observe(scroller, { childList: true, subtree: true });
    scroller.addEventListener('load', toBottom, true); // ảnh tải xong
    for (const ev of ['wheel', 'touchmove', 'keydown', 'pointerdown']) scroller.addEventListener(ev, stop, { passive: true });
    const timer = setTimeout(stop, 4000);
    toBottom();
    return () => {
      clearTimeout(timer);
      mo.disconnect();
      scroller.removeEventListener('load', toBottom, true);
      for (const ev of ['wheel', 'touchmove', 'keydown', 'pointerdown']) scroller.removeEventListener(ev, stop);
    };
    // Chỉ chạy một lần khi mở (ConversationView được tạo lại cho mỗi hội thoại: key = id).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Dải tin ghim (W2.1) hiện/ẩn, đổi tab → cuộn lại xuống cuối.
  useEffect(() => {
    // Không trả kết quả: scrollIntoView() trả Promise ở Chrome mới, React coi đó là cleanup sai.
    if (!focusMessage || focused.current === focusMessage.n) bottom.current?.scrollIntoView({ block: 'end' });
  }, [focusMessage, pinned.length > 0, tab]);
  // Tin mới: chỉ kéo xuống khi đang ở gần đáy hoặc là tin của mình (đang đọc tin cũ thì giữ nguyên vị trí).
  const lastMessage = messages.at(-1);
  useEffect(() => {
    if (focusMessage && focused.current !== focusMessage.n) return;
    if (nearBottom.current || lastMessage?.sender_user_id === client.userId) bottom.current?.scrollIntoView({ block: 'end' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages.length]);

  // A5/A6: đang mở hội thoại và tab hiển thị → đánh dấu đã đọc (gửi receipt `read` khi có tin mới). Chỉ chạy lại khi
  // có tin mới hơn (seq lớn nhất đổi), không theo mỗi lần tải lại danh sách (reaction, receipt…) — receipt gửi lỗi
  // do SDK tự gửi lại có backoff.
  // Tin trả lời trong luồng (W2.2) không có ở dòng chính → chạy lại cả khi số chưa đọc của hội thoại tăng.
  const lastSeq = messages.reduce((n, m) => (m.seq !== null && m.seq > n ? m.seq : n), 0);
  const unread = conversation.unread_count;
  useEffect(() => {
    if (visible && (lastSeq || unread) && !conversation.left) void client.markRead(conversation.id).catch(() => undefined);
  }, [client, conversation.id, conversation.left, lastSeq, unread, visible]);

  const byId = useMemo(() => new Map(messages.map((m) => [m.id, m])), [messages]);

  // A1: tin gốc của trả lời nằm ngoài danh sách đang tải (cũ hơn 200 tin cuối) → lấy thẳng từ store cục bộ.
  const [olderQuotes, setOlderQuotes] = useState<Map<string, LocalMessage | null>>(new Map());
  useEffect(() => {
    const missing = [...new Set(messages.map((m) => m.reply_to).filter((id): id is string => !!id && !byId.has(id)))];
    if (!missing.length) return;
    let alive = true;
    void Promise.all(missing.map(async (id) => [id, (await client.getMessage(conversation.id, id)) ?? null] as const)).then(
      (found) => alive && setOlderQuotes(new Map(found)),
    );
    return () => {
      alive = false;
    };
  }, [client, conversation.id, messages, byId]);
  const quoteOf = (id: string) => byId.get(id) ?? olderQuotes.get(id) ?? null;

  /** Tin cần cuộn tới khi đã có trong danh sách (sau khi `loadUntil` tải thêm lịch sử). */
  const [scrollTarget, setScrollTarget] = useState<string>();
  useEffect(() => {
    if (!scrollTarget || !byId.has(scrollTarget)) return;
    setScrollTarget(undefined);
    requestAnimationFrame(() => void scrollTo(scrollTarget));
  }, [scrollTarget, byId]);

  /**
   * Cuộn tới tin `id`; chưa tải thì tải thêm lịch sử tới nó; tin trong luồng → mở khung luồng. Trả false nếu tin không
   * có trên thiết bị.
   */
  async function reveal(id: string): Promise<boolean> {
    setTab('chat');
    if (byId.has(id)) return scrollTo(id);
    const m = await client.getMessage(conversation.id, id);
    if (!m) return false;
    if (m.thread_root) {
      setThread(m.thread_root);
      return true;
    }
    loadUntil(id);
    setScrollTarget(id);
    return true;
  }

  function scrollTo(id: string) {
    const el = document.getElementById(`msg-${id}`);
    if (!el) return false;
    el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    setHighlight(id);
    setTimeout(() => setHighlight((h) => (h === id ? undefined : h)), 2000);
    return true;
  }

  // D1: mở đúng tin từ kết quả tìm kiếm (chờ danh sách tin tải xong; tin cũ hơn 200 tin cuối thì tải thêm lịch sử).
  useEffect(() => {
    if (!focusMessage || focused.current === focusMessage.n || !messages.length) return;
    focused.current = focusMessage.n;
    if (byId.has(focusMessage.id)) requestAnimationFrame(() => void scrollTo(focusMessage.id));
    else void reveal(focusMessage.id).then((ok) => ok || setError('Tin không còn trên thiết bị này'));
  }, [focusMessage, byId, messages.length]);

  const other = conversation.kind === 'direct' ? conversation.members.find((m) => m.user_id !== client.userId) : undefined;
  const members = useMemo(
    () =>
      conversation.members
        .filter((m) => m.user_id !== client.userId)
        .map((m) => ({
          id: m.user_id,
          name: client.getUser(m.user_id)?.display_name,
        }))
        .filter((m): m is { id: string; name: string } => !!m.name),
    [client, conversation.members],
  );

  async function submit(value: string, mentions: string[], entities: TextEntity[]) {
    setError(undefined);
    try {
      if (editing) {
        await client.editText(conversation.id, editing.id, value, {
          mentions,
          entities,
        });
        setEditing(undefined);
      } else {
        const reply = replyTo?.id;
        setReplyTo(undefined);
        await client.sendText(conversation.id, value, {
          replyTo: reply,
          mentions,
          entities,
        });
      }
    } catch (err) {
      setError(sendErrorText(err));
    }
  }

  /** Mã an toàn (1-1): bấm "Xác minh" lần nữa thì ẩn; luôn mở khung thông tin để hiện mã. */
  async function verify() {
    if (!other) return;
    if (safety && contextOpen) return setSafety(undefined);
    onToggleContext(true);
    setSafety(await client.safetyNumber(other.user_id));
  }

  /** Chỉ báo "đã xem" hiển thị ở tin cuối cùng của mình đã gửi xong. */
  const lastMine = messages.findLast((m) => m.sender_user_id === client.userId && m.seq !== null && !m.deleted)?.id;
  const names = useMemo(() => members.map((m) => m.name).concat(client.displayName ?? [], conversation.kind === 'direct' ? [] : ['all']), [members, client.displayName, conversation.kind]);
  const canManageMembers = conversation.kind !== 'direct' && !conversation.left;
  /** Nhóm đã rời: xóa khỏi danh sách của mình (lịch sử bị xóa trên mọi thiết bị của mình). */
  const removeLeft = async () => {
    if (!window.confirm(`Xóa nhóm đã rời “${conversation.title}” khỏi danh sách?\n\nLịch sử nhóm sẽ bị xóa khỏi tài khoản của bạn trên mọi thiết bị, không thể khôi phục. Các thành viên khác không bị ảnh hưởng.`)) return;
    try {
      await client.hideConversation(conversation.id);
      onBack();
    } catch {
      // Lỗi mạng: hội thoại còn nguyên, thử lại sau.
    }
  };

  const senderName = (id: string) => client.getUser(id)?.display_name;
  const bubbleProps = (m: LocalMessage): Omit<BubbleProps, 'continued'> => ({
    m,
    me: client.userId,
    mine: m.sender_user_id === client.userId,
    direct: conversation.kind === 'direct',
    senderName,
    senderIsBot: client.getUser(m.sender_user_id)?.kind === 'bot',
    names,
    quoted: m.reply_to ? quoteOf(m.reply_to) : undefined,
    mentioned: client.mentionsMe(m),
    highlighted: highlight === m.id || searchHit === m.id,
    readOnly: conversation.left,
    seen: m.id === lastMine ? receiptLabel(m, receipts, client.userId, conversation.kind === 'direct') : undefined,
    pinned: pinnedIds.has(m.id),
    thread: threadOf.get(m.id),
    onQuoteClick: () => m.reply_to && void reveal(m.reply_to).then((ok) => ok || setError('Tin gốc không còn trên thiết bị này')),
    onReply: () => (setReplyTo(m), setEditing(undefined), input.current?.focus()),
    onOpenThread: () => setThread(m.id),
    onPin: canPin ? () => void client.pin(conversation.id, m.id, pinnedIds.has(m.id)).catch((e) => setError(sendErrorText(e))) : undefined,
    onImportant:
      m.sender_user_id === client.userId
        ? () => void client.setImportant(conversation.id, m.id, !m.important).catch((e) => setError(sendErrorText(e)))
        : undefined,
    onForward: conversation.secret ? undefined : () => setForwarding(m),
    onReact: (emoji, remove) => void client.react(conversation.id, m.id, emoji, remove).catch((e) => setError(String(e))),
    onEdit: () => (setEditing(m), setReplyTo(undefined), setTab('chat')),
    onDelete: () => void client.deleteMessage(conversation.id, m.id),
    onResend: () => {
      setError(undefined);
      void client.resendMessage(conversation.id, m.id).catch((e) => setError(sendErrorText(e)));
    },
  });

  return (
    <>
      <section className="ws-main" aria-label={`Hội thoại ${conversation.title}`}>
        <ConversationHeader
          conversation={conversation}
          project={project}
          typing={typing}
          contextOpen={contextOpen}
          tab={tab}
          threadCount={threads.length}
          onTab={setTab}
          onBack={onBack}
          onVerify={other ? () => void verify() : undefined}
          onMembers={canManageMembers ? () => setManaging(true) : undefined}
          onDelete={conversation.left && conversation.kind !== 'direct' ? () => void removeLeft() : undefined}
          onToggleContext={() => onToggleContext(!contextOpen)}
          searchOpen={searching}
          onToggleSearch={() => (setSearching((v) => !v), setSearchHit(undefined))}
        />
        {tab === 'chat' && searching && (
          <ConversationSearch
            conversationId={conversation.id}
            onReveal={(id) => {
              setSearchHit(id);
              void reveal(id);
            }}
            onClose={() => (setSearching(false), setSearchHit(undefined))}
          />
        )}
        {conversation.warning && (
          <div className="ws-warning" role="alert">
            ⚠ {conversation.warning}
          </div>
        )}
        {tab === 'chat' && (
          <PinnedBar
            pinned={pinned}
            senderName={senderName}
            onOpen={(p) => void reveal(p.id).then((ok) => ok || setError('Tin không còn trên thiết bị này'))}
          />
        )}
        {tab === 'threads' && (
          <div className="ws-tab-pane" role="tabpanel" aria-label="Luồng trao đổi">
            <ThreadList conversationId={conversation.id} threads={threads} senderName={senderName} onOpen={setThread} />
          </div>
        )}
        {tab === 'files' && (
          <div className="ws-tab-pane" role="tabpanel" aria-label="Tệp">
            <FilesTab files={files} me={client.userId} noAccess={!!conversation.left} senderName={senderName} onOpenThread={setThread} />
          </div>
        )}
        {tab === 'activity' && (
          <div className="ws-tab-pane" role="tabpanel" aria-label="Hoạt động">
            <ActivityList activity={activity} me={client.userId} senderName={senderName} />
          </div>
        )}
        {tab === 'chat' && (
          <ImageAlbum messages={messages} senderName={senderName}>
            <MessageList messages={messages} dragOver={dragOver} drop={drop} bottomRef={bottom} bubble={bubbleProps} hasMore={older.hasMore} onLoadMore={older.loadMore} onNearBottom={(near) => (nearBottom.current = near)} />
          </ImageAlbum>
        )}
        {tab === 'chat' && (
          <Composer
            conversation={conversation}
            members={members}
            projects={projects}
            editing={editing}
            replyTo={replyTo}
            error={error}
            upload={upload}
            inputRef={input}
            drop={drop}
            stageRef={stage}
            onCancelEdit={() => setEditing(undefined)}
            onCancelReply={() => setReplyTo(undefined)}
            onFiles={(files) => sendFiles(files)}
            onSubmit={submit}
          />
        )}
      </section>

      {thread ? (
        <ThreadPanel
          key={thread}
          conversation={conversation}
          rootId={thread}
          drawer={drawer}
          bubble={bubbleProps}
          upload={upload}
          onFiles={(files, onError) => void sendFiles(files, thread, onError)}
          onClose={() => setThread(undefined)}
        />
      ) : (
        contextOpen && (
          <ConversationContext
            conversation={conversation}
            project={project}
            safety={safety}
            drawer={drawer}
            onVerify={other ? () => void verify() : undefined}
            onManage={canManageMembers ? () => setManaging(true) : undefined}
            onClose={() => onToggleContext(false)}
            onOpenExternal={onOpenExternal}
          />
        )
      )}

      {managing && <LazyGroupDialog conversation={conversation} onClose={() => setManaging(false)} />}
      {forwarding && <LazyForwardDialog message={forwarding} from={conversation} conversations={conversations} onClose={() => setForwarding(undefined)} />}
    </>
  );
}
